import { useEffect, useState } from "react";
import { Cog, Trash2, ExternalLink, History, ArrowRight, AlertTriangle, Save, Pencil, X, CalendarClock, ChevronDown, Plus, Loader2, ShoppingCart, MoreHorizontal, SplitSquareHorizontal, Combine, FolderTree, FolderInput, CheckSquare, Square, FolderPlus, Wrench, Archive, Recycle, GitBranch, ArrowLeftRight, RotateCcw } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import ConfirmModal from "../ConfirmModal";
import DateTimePicker from "../DateTimePicker";
import { toast } from "../ui/sonner";
import AddEquipmentSheet from "./AddEquipmentSheet";
import RecordServiceModal from "./RecordServiceModal";
import DictTextarea from "../DictTextarea";

export default function EquipmentBlock({
  equipment,
  serviceLogs,
  sites,
  canEdit,
  onChanged,
  defaultOpen = false,
  isPortal = false,
  // Iter 397 — when block is embedded on SiteDetail we already know
  // the site id; pass it through so the "+ Add equipment" modal
  // pre-fills and locks the site field.
  presetSiteId = null,
  customerId = null,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false); // Iter 397
  // Iter 418.x (Dirk, 2026-02-28) — "+ Group" flow: create a new group
  // from scratch (not by converting an existing row). User names the
  // group, then picks any equipment on that site to nest under it.
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  // Iter 425.f — Bulk service recording. `svcModal` = { siteId,
  // presetIds[] } or null. Opened by the site-level button or by a
  // per-row quick-service action.
  const [svcModal, setSvcModal] = useState(null);
  // Iter 397 — customer-level view supports optional site filter dropdown.
  // Site-level (presetSiteId set) skips the dropdown entirely.
  const [siteFilter, setSiteFilter] = useState("all");

  const siteOf = (sid) => sites.find((s) => s.id === sid);

  // Iter 397 — apply optional site filter (customer-level view).
  const visibleEquipment = presetSiteId
    ? equipment
    : (siteFilter === "all" ? equipment : equipment.filter((e) => e.site_id === siteFilter));

  // group equipment by site
  const grouped = {};
  visibleEquipment.forEach((eq) => {
    const k = eq.site_id || "_orphan";
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(eq);
  });
  const sortedSiteIds = Object.keys(grouped).sort((a, b) => {
    const an = siteOf(a)?.name || "";
    const bn = siteOf(b)?.name || "";
    return an.localeCompare(bn);
  });

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/equipment/${confirmDel.id}`);
      setConfirmDel(null);
      setExpandedId(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const dueClass = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    if (diff < 0) return "text-rose-300 border-rose-500/40 bg-rose-500/10";
    if (diff < 30) return "text-amber-300 border-amber-500/40 bg-amber-500/10";
    return "text-[#a0aabf] border-white/10 bg-white/[0.02]";
  };

  return (
    <>
    <FoldBlock
      icon={Cog}
      iconColor="#10b981"
      title="Equipment & service intervals"
      count={equipment.length}
      defaultOpen={defaultOpen}
      navigateTo={isPortal ? undefined : "/scheduler"}
      navigateLabel="Service Schedule"
      testId="block-equipment"
      emptyHint="No equipment installed at this customer's sites."
      canEdit={canEdit && !isPortal}
      onAdd={() => setAddOpen(true)}
      addLabel="Add equipment"
    >
      {/* Iter 418.x (Dirk, 2026-02-28) — "+ Group" button. Creates a
          brand-new group container, then lets the user pick which
          equipment to add. Sibling of the "+ Add equipment" affordance
          FoldBlock already gives us. */}
      {canEdit && !isPortal && equipment.length > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setNewGroupOpen(true)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="equipment-new-group-btn"
            title="Create a new group container (e.g. AFM FRP) and nest existing equipment inside it"
          >
            <FolderPlus className="w-3.5 h-3.5" /> New group
          </button>
          {/* Iter 425.f — Bulk service recording. Site-scoped view
              (presetSiteId set) opens the modal immediately for that
              site. Customer-scoped view falls back to prompting for
              site via the site filter first. */}
          {(presetSiteId || siteFilter !== "all") && (
            <button
              type="button"
              onClick={() => setSvcModal({
                siteId: presetSiteId || siteFilter,
                presetIds: [],
              })}
              className="hv-btn text-xs"
              data-testid="equipment-record-service-btn"
              title="Record a service against one or more items on this site"
            >
              <Wrench className="w-3.5 h-3.5" /> Record service
            </button>
          )}
          <span className="text-[10px] text-[#647087]">
            Groups let you tick &quot;AFM FRP&quot; once and see every child (Filter head, FRP, Riser, AFM 1/2/3…) come along.
          </span>
        </div>
      )}
      {/* Iter 397 — Site filter dropdown (customer-level view only). */}
      {!presetSiteId && sites.length > 1 && (
        <div className="mb-3 flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Filter by site:</label>
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="text-xs bg-[#0a0b0e] border border-white/10 rounded px-2 py-1 text-white"
            data-testid="equipment-site-filter"
          >
            <option value="all">All sites ({equipment.length})</option>
            {sites.map((s) => {
              const cnt = equipment.filter((e) => e.site_id === s.id).length;
              if (cnt === 0) return null;
              return <option key={s.id} value={s.id}>{s.name} ({cnt})</option>;
            })}
          </select>
        </div>
      )}
      <div className="space-y-2">
        {sortedSiteIds.map((sid) => {
          const site = siteOf(sid);
          return (
            <SiteEquipmentGroup
              key={sid}
              siteId={sid}
              siteName={site?.name || "Unassigned site"}
              items={grouped[sid]}
              dueClass={dueClass}
              expandedId={expandedId}
              onToggle={(eq) => setExpandedId((cur) => (cur === eq.id ? null : eq.id))}
              serviceLogs={serviceLogs}
              siteOf={siteOf}
              canEdit={canEdit}
              isPortal={isPortal}
              onChanged={onChanged}
              onDelete={(eq) => setConfirmDel(eq)}
              onQuickService={(eq) => setSvcModal({
                siteId: eq.site_id, presetIds: [eq.id],
              })}
            />
          );
        })}
      </div>

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Delete equipment"
        message={
          <>
            Permanently delete <b>{confirmDel?.name}</b>? Service-history records
            for this item will remain.
          </>
        }
        confirmLabel="Delete equipment"
        testId="equipment-delete-confirm"
        busy={busy}
      />
    </FoldBlock>

    {/* Iter 397d — modal MUST live outside FoldBlock. Previously it
        sat inside children which FoldBlock skips whenever count=0
        (empty-state branch), so clicking "+ Add equipment" set
        addOpen=true but the modal component never mounted. Hoisting
        the render up here makes the empty-state path work. */}
    {addOpen && (
      <AddEquipmentSheet
        sites={sites}
        presetSiteId={presetSiteId}
        customerId={customerId || (equipment[0] && equipment[0].customer_id) || null}
        onClose={() => setAddOpen(false)}
        onCreated={() => { setAddOpen(false); onChanged?.(); }}
      />
    )}
    {/* Iter 418.x — free-standing "New group" modal. */}
    {newGroupOpen && (
      <NewGroupModal
        sites={sites}
        presetSiteId={presetSiteId}
        equipment={equipment}
        customerId={customerId || (equipment[0] && equipment[0].customer_id) || null}
        onClose={() => setNewGroupOpen(false)}
        onCreated={() => { setNewGroupOpen(false); onChanged?.(); }}
      />
    )}
    {/* Iter 425.f — Bulk service recording modal. */}
    {svcModal && (
      <RecordServiceModal
        equipment={equipment}
        siteId={svcModal.siteId}
        siteName={siteOf(svcModal.siteId)?.name}
        customerId={customerId || (equipment[0] && equipment[0].customer_id) || null}
        presetEquipmentIds={svcModal.presetIds || []}
        onClose={() => setSvcModal(null)}
        onSaved={() => { setSvcModal(null); onChanged?.(); }}
      />
    )}
    </>
  );
}

function SiteEquipmentGroup({
  siteId, siteName, items, dueClass,
  expandedId, onToggle,
  serviceLogs, siteOf, canEdit, isPortal, onChanged, onDelete,
  onQuickService,
}) {
  const [open, setOpen] = useState(true);
  // Iter 426.p9 (2026-05-11) — Per-group collapse. Dirk: *"Allow
  // equipment groups to fold and unfold."* Each group container
  // (is_group=true) gets its own chevron; clicking it hides just
  // that group's children without collapsing the whole site. State
  // persists in localStorage per site so the choice survives a
  // reload.
  const _storageKey = `hv_eqgroup_collapsed_${siteId}`;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(_storageKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (_e) { return new Set(); }
  });
  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      try { localStorage.setItem(_storageKey, JSON.stringify([...next])); } catch (_e) {}
      return next;
    });
  };
  return (
    <div className="border border-white/5 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-2">
          <ArrowRight
            className={`w-3 h-3 text-[#647087] transition-transform ${open ? "rotate-90" : ""}`}
          />
          <span className="text-sm font-bold">{siteName}</span>
          <span className="text-xs text-[#647087]">{items.length}</span>
        </div>
      </button>
      {open && (
        <div className="divide-y divide-white/5">
          {(() => {
            // Iter 418.u (Dirk, 2026-02-28) — nest children under their
            // parent group. Order: [orphan A, orphan B, parent X,
            // child X1, child X2, parent Y, child Y1…]. Every child
            // gets a small left-indent + a rail so the hierarchy is
            // visually obvious without any drag-drop tree component.
            const byParent = new Map();
            const roots = [];
            for (const eq of items) {
              if (eq.group_of && items.some((p) => p.id === eq.group_of)) {
                const arr = byParent.get(eq.group_of) || [];
                arr.push(eq);
                byParent.set(eq.group_of, arr);
              } else {
                roots.push(eq);
              }
            }
            const flat = [];
            for (const r of roots) {
              flat.push({ eq: r, depth: 0 });
              // Iter 426.p9 — if this group is collapsed, skip its
              // children so the row list collapses cleanly.
              if (r.is_group && collapsedGroups.has(r.id)) continue;
              const kids = byParent.get(r.id) || [];
              for (const k of kids) flat.push({ eq: k, depth: 1 });
            }
            return flat;
          })().map(({ eq, depth }) => {
            const expanded = expandedId === eq.id;
            const detailLogs = serviceLogs.filter((l) => l.site_equipment_id === eq.id);
            const isCollapsedGroup = eq.is_group && collapsedGroups.has(eq.id);
            const groupChildCount = eq.is_group
              ? (items.filter((x) => x.group_of === eq.id).length)
              : 0;
            return (
              <div
                key={eq.id}
                data-testid={`equipment-row-${eq.id}`}
                className={depth > 0 ? "pl-6 border-l-2 border-cyan-500/25 ml-3" : ""}
              >
                <button
                  type="button"
                  onClick={() => onToggle(eq)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${expanded ? "bg-emerald-500/10" : "hover:bg-emerald-500/5"}`}
                  data-testid={`equipment-row-toggle-${eq.id}`}
                  aria-expanded={expanded}
                >
                  <div className="min-w-0 flex-1 flex items-center gap-2">
                    {/* Iter 426.p9 — Group-level fold chevron. Only on
                        parent group rows. Clicking it collapses the
                        group's children WITHOUT opening the detail
                        panel (stopPropagation on the wrapping row). */}
                    {eq.is_group ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(ev) => { ev.stopPropagation(); toggleGroup(eq.id); }}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            ev.stopPropagation();
                            toggleGroup(eq.id);
                          }
                        }}
                        className="p-0.5 -m-0.5 rounded hover:bg-cyan-500/20 text-cyan-200 shrink-0 cursor-pointer"
                        aria-label={isCollapsedGroup ? "Expand group" : "Collapse group"}
                        title={isCollapsedGroup ? "Show group items" : "Hide group items"}
                        data-testid={`equipment-group-fold-${eq.id}`}
                      >
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${isCollapsedGroup ? "-rotate-90" : "rotate-0"}`}
                        />
                      </span>
                    ) : (
                      <ChevronDown className={`w-3 h-3 text-[#647087] transition-transform shrink-0 ${expanded ? "rotate-0" : "-rotate-90"}`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {/* Iter 418.u — parent-group rows render as a
                            label chip so their role is obvious. */}
                        {eq.is_group && (
                          <span
                            className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 shrink-0"
                            title="Group container — real equipment lives underneath"
                          >
                            Group
                          </span>
                        )}
                        <span className="truncate">{eq.name}</span>
                        {/* Iter 426.p9 — child count chip on collapsed
                            groups so ops sees how many items are
                            hidden. */}
                        {eq.is_group && (
                          <span
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-200 border border-cyan-500/30 shrink-0"
                            data-testid={`equipment-group-count-${eq.id}`}
                            title={`${groupChildCount} item(s) inside — open the detail to rename or move items in / out`}
                          >
                            {groupChildCount} item{groupChildCount === 1 ? "" : "s"}
                          </span>
                        )}
                        {/* Iter 426.p9 — Explicit "Manage" pencil on
                            group rows so the rename / add-remove
                            affordances are discoverable. Same click
                            target as the whole row (opens the detail
                            panel) but visually screams "editable". */}
                        {eq.is_group && canEdit && !isPortal && (
                          <span
                            className="p-1 rounded hover:bg-cyan-500/25 text-cyan-200 shrink-0"
                            title="Rename group, add/remove items"
                            data-testid={`equipment-group-manage-${eq.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </span>
                        )}
                        {/* Iter 418.u — inline identifier chip, always
                            visible. Distinguishes "RO 1 Bag Filter"
                            from the twin "RO 2 Bag Filter" without
                            polluting the canonical name. */}
                        {eq.identifier && (
                          <span
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 shrink-0"
                            data-testid={`equipment-identifier-${eq.id}`}
                          >
                            {eq.identifier}
                          </span>
                        )}
                        {/* Iter 418.u — non-serviceable child chip so
                            admin sees at a glance which parts don't
                            get their own service tick. */}
                        {eq.group_of && eq.serviceable === false && (
                          <span
                            className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded border border-white/10 bg-white/5 text-[#647087] shrink-0"
                            title="Not individually serviceable"
                          >
                            no-svc
                          </span>
                        )}
                        {/* Iter 427.p48 (WS3) — Replaced / repaired
                            badge on any standalone (non-group,
                            non-child) equipment marked as
                            non-serviceable. Signals to ops the item
                            is out of the normal service rotation but
                            still visible in the master list. */}
                        {!eq.group_of && !eq.is_group && eq.serviceable === false && (
                          <span
                            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0 inline-flex items-center gap-1"
                            title="Marked replaced or repaired — hidden from Record Service by default"
                            data-testid={`equipment-replaced-tag-${eq.id}`}
                          >
                            <Wrench className="w-2.5 h-2.5" />
                            Replaced/Repaired
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#647087] truncate">
                        {eq.quantity || 0} {eq.unit || "pcs"}
                        {eq.manufacturer ? ` · ${eq.manufacturer}` : ""}
                        {eq.group_of ? " · in group" : ""}
                        {eq.inventory_item_id ? (
                          <span className="text-emerald-400" title={eq.inventory_name || "Linked to stock"} data-testid={`equipment-stock-link-${eq.id}`}>
                            {` · Stock: ${eq.inventory_sku || eq.inventory_name || "linked"}`}
                          </span>
                        ) : (!eq.is_group && (
                          <span className="text-amber-400/80" data-testid={`equipment-nonstock-${eq.id}`}> · Non-stock</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Iter 398 — Portal hides the service-schedule badge
                      entirely per Dirk: "Ensure portal only shows
                      equipment per site and history. No schedule." */}
                  {!isPortal && eq.is_service_item && eq.next_service_date && (
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border whitespace-nowrap shrink-0 ${dueClass(eq.next_service_date)}`}
                    >
                      {new Date(eq.next_service_date) < new Date() && (
                        <AlertTriangle className="inline w-2.5 h-2.5 mr-0.5" />
                      )}
                      {new Date(eq.next_service_date).toLocaleDateString('en-GB')}
                    </span>
                  )}
                  {/* Iter 425.f — Quick service shortcut on each row.
                      Opens the same bulk-service modal pre-checked
                      with this one equipment. Hidden for portal + for
                      group containers (parent rows) + non-serviceable
                      children. */}
                  {!isPortal && canEdit && onQuickService
                    && !eq.is_group && eq.serviceable !== false && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); onQuickService(eq); }}
                      className="p-1 rounded hover:bg-emerald-500/20 text-emerald-300 shrink-0"
                      title="Quick record service"
                      data-testid={`equipment-quick-service-${eq.id}`}
                    >
                      <Wrench className="w-3 h-3" />
                    </button>
                  )}
                </button>
                {expanded && (
                  <div className="px-3 py-3 border-t border-emerald-500/20 bg-emerald-500/[0.03]">
                    <EquipmentDetailBody
                      detail={eq}
                      siteOf={siteOf}
                      detailLogs={detailLogs}
                      canEdit={canEdit}
                      isPortal={isPortal}
                      onChanged={onChanged}
                      onClose={() => onToggle(eq)}
                      onDelete={() => onDelete(eq)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      <div className="text-sm text-[#dde3ec]">{value}</div>
    </div>
  );
}

/* ----------------------------------------------------------------
 * Inline detail body — Wave 7-fix #9 (now inline-expand, not modal)
 * Customer→Inventory tab is the SINGLE master editor for service
 * intervals + next/last service dates + the is_service_item flag.
 * Service Schedule page is purely a calculated view.
 * ---------------------------------------------------------------- */
function EquipmentDetailBody({ detail: initialDetail, siteOf, detailLogs, canEdit, isPortal, onChanged, onClose, onDelete }) {
  const [detail, setDetail] = useState(initialDetail);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const [form, setForm] = useState({
    is_service_item: !!initialDetail.is_service_item,
    interval_days: initialDetail.interval_days || "",
    next_service_date: initialDetail.next_service_date || "",
    last_service_date: initialDetail.last_service_date || "",
    install_date: initialDetail.install_date || "",
    quantity: initialDetail.quantity ?? 1,
    unit: initialDetail.unit || "pcs",
    notes: initialDetail.notes || "",
  });
  // Iter 418.u — inline identifier + split/merge modals.
  const [identifier, setIdentifier] = useState(initialDetail.identifier || "");
  const [identifierBusy, setIdentifierBusy] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [showMoveGroup, setShowMoveGroup] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  // Iter 427.p48 (WS4, Dirk 2026-02-20) — lifecycle modals.
  const [showRetire, setShowRetire] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [showLineage, setShowLineage] = useState(false);

  const patchEquipment = async (patch, opts = {}) => {
    try {
      const { data } = await api.patch(`/equipment/${detail.id}`, patch);
      setDetail((prev) => ({ ...prev, ...(data || {}), ...patch }));
      if (!opts.silent) toast.success("Saved");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const saveIdentifier = async () => {
    const trimmed = (identifier || "").trim();
    if ((initialDetail.identifier || "") === trimmed) return;
    setIdentifierBusy(true);
    await patchEquipment({ identifier: trimmed || null }, { silent: true });
    setIdentifierBusy(false);
  };

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const patch = {
        is_service_item: !!form.is_service_item,
        interval_days: form.interval_days ? Number(form.interval_days) : null,
        next_service_date: form.next_service_date || null,
        last_service_date: form.last_service_date || null,
        install_date: form.install_date || null,
        quantity: form.quantity === "" ? null : Number(form.quantity),
        unit: form.unit || "pcs",
        notes: form.notes ?? "",
      };
      const { data } = await api.patch(`/equipment/${detail.id}`, patch);
      setDetail((prev) => ({ ...prev, ...(data || {}) }));
      setEdit(false);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="equipment-detail-content">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
          {isPortal ? "Equipment" : (form.is_service_item ? "Service item" : "Equipment")}
        </span>
        {/* Iter 427.p48 (WS4) — Retired badge with retire audit. */}
        {detail.retired_at && (
          <span
            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/10 inline-flex items-center gap-1"
            title={
              `Retired on ${new Date(detail.retired_at).toLocaleDateString('en-GB')}` +
              (detail.retired_reason ? ` · ${detail.retired_reason}` : "") +
              (detail.retired_by_user_name ? ` · by ${detail.retired_by_user_name}` : "")
            }
            data-testid="equipment-retired-badge"
          >
            <Archive className="w-2.5 h-2.5" />
            Retired
          </span>
        )}
        {detail.external_ref && (
          <span className="text-[10px] font-mono text-[#647087]">
            {detail.external_ref}
          </span>
        )}
        <span className="text-[10px] text-[#647087]">
          Site: {siteOf(detail.site_id)?.name || "—"}
        </span>
        {isPortal && (
          <button
            type="button"
            onClick={() => {
              // Iter 398 — Portal reorder: deep-link to the order page,
              // preserve the site & inventory context so the client
              // lands on their catalogue with the item highlighted.
              const params = new URLSearchParams();
              if (detail.customer_id) params.set("customer_id", detail.customer_id);
              if (detail.site_id) params.set("site_id", detail.site_id);
              if (detail.id) params.set("prefill_equipment_id", detail.id);
              navigate(`/portal/order-new?${params.toString()}`);
            }}
            className="hv-btn hv-btn-primary text-xs ml-auto"
            data-testid={`equipment-portal-reorder-${detail.id}`}
          >
            <ShoppingCart className="w-3.5 h-3.5" /> Reorder
          </button>
        )}
        {canEdit && !edit && !isPortal && (
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setEdit(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="equipment-edit-btn"
              title="Edit service schedule + install/interval fields"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit schedule
            </button>
            {/* Iter 418.u — More actions: split / merge / group. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setActionsOpen((v) => !v)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="equipment-more-actions-btn"
                title="Split, merge, or group this equipment"
                aria-expanded={actionsOpen}
              >
                <MoreHorizontal className="w-3.5 h-3.5" /> Actions
              </button>
              {actionsOpen && (
                <>
                  <button
                    type="button"
                    onClick={() => setActionsOpen(false)}
                    className="fixed inset-0 z-30 cursor-default"
                    aria-hidden
                  />
                  <div
                    className="absolute right-0 mt-1 z-40 min-w-[220px] rounded-md border border-white/10 bg-[#151821] shadow-xl py-1 text-sm hv-inv-dropdown"
                    data-testid="equipment-actions-menu"
                  >
                    <MenuItem
                      onClick={() => { setActionsOpen(false); setShowSplit(true); }}
                      icon={SplitSquareHorizontal}
                      label="Split into multiple…"
                      hint="Break one row into per-unit rows"
                      testId="equipment-action-split"
                    />
                    <MenuItem
                      onClick={() => { setActionsOpen(false); setShowMerge(true); }}
                      icon={Combine}
                      label="Merge with another…"
                      hint="Combine duplicates into one row"
                      testId="equipment-action-merge"
                    />
                    <MenuItem
                      onClick={() => {
                        setActionsOpen(false);
                        patchEquipment({ is_group: !detail.is_group });
                      }}
                      icon={detail.is_group ? Cog : FolderTree}
                      label={detail.is_group ? "Turn back into equipment" : "Turn into group label"}
                      hint={detail.is_group ? "Return to a normal equipment row" : "Use this row as a container for children"}
                      testId="equipment-action-toggle-group"
                    />
                    <MenuItem
                      onClick={() => { setActionsOpen(false); setShowMoveGroup(true); }}
                      icon={FolderInput}
                      label={detail.group_of ? "Move to another group…" : "Add to a group…"}
                      hint={detail.group_of ? "Change parent or detach" : "Nest under a parent equipment"}
                      testId="equipment-action-move-group"
                    />
                    {detail.group_of && (
                      <MenuItem
                        onClick={() => {
                          setActionsOpen(false);
                          patchEquipment({ serviceable: detail.serviceable === false });
                        }}
                        icon={detail.serviceable === false ? CheckSquare : Square}
                        label={detail.serviceable === false ? "Mark serviceable" : "Mark not-serviceable"}
                        hint="Only serviceable children appear on service tick-lists"
                        testId="equipment-action-toggle-serviceable"
                      />
                    )}
                    {/* Iter 427.p48 (WS3, Dirk 2026-02-20) — Same
                        serviceable toggle for non-group items so the
                        whole "hide replaced/broken items from the
                        service dropdown" flow works everywhere. */}
                    {!detail.group_of && !detail.is_group && (
                      <MenuItem
                        onClick={() => {
                          setActionsOpen(false);
                          patchEquipment({ serviceable: detail.serviceable === false });
                        }}
                        icon={detail.serviceable === false ? CheckSquare : Wrench}
                        label={detail.serviceable === false ? "Restore as serviceable" : "Mark replaced / repaired"}
                        hint="Non-serviceable items are hidden from Record Service by default"
                        testId="equipment-action-toggle-serviceable-standalone"
                      />
                    )}
                    {/* Iter 427.p48 (WS4, Dirk 2026-02-20) — Lifecycle
                        actions. Retire and Replace are DELIBERATELY
                        kept as two separate menu items per Dirk's
                        UX call. Lineage opens a read-only timeline
                        of every predecessor + successor. */}
                    {!detail.is_group && !detail.retired_at && (
                      <>
                        <MenuItem
                          onClick={() => { setActionsOpen(false); setShowRetire(true); }}
                          icon={Archive}
                          label="Retire equipment"
                          hint="Decommission this item — kept in history"
                          testId="equipment-action-retire"
                        />
                        <MenuItem
                          onClick={() => { setActionsOpen(false); setShowReplace(true); }}
                          icon={Recycle}
                          label="Replace with new…"
                          hint="Retire this and create a successor row"
                          testId="equipment-action-replace"
                        />
                      </>
                    )}
                    {!detail.is_group && detail.retired_at && (
                      <MenuItem
                        onClick={async () => {
                          setActionsOpen(false);
                          try {
                            const { data } = await api.post(`/equipment/${detail.id}/unretire`);
                            setDetail((prev) => ({ ...prev, ...(data || {}) }));
                            toast.success("Equipment restored");
                            onChanged?.();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Could not restore");
                          }
                        }}
                        icon={RotateCcw}
                        label="Un-retire"
                        hint="Bring this equipment back into active rotation"
                        testId="equipment-action-unretire"
                      />
                    )}
                    {!detail.is_group && (detail.replacement_of_equipment_id || detail.replaced_by_equipment_id) && (
                      <MenuItem
                        onClick={() => { setActionsOpen(false); setShowLineage(true); }}
                        icon={GitBranch}
                        label="View lineage"
                        hint="See every predecessor + successor"
                        testId="equipment-action-lineage"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Iter 418.u — Inline identifier, always visible. Distinguishes
          "Bag Filter" imported from an invoice from "RO 1 Bag Filter"
          when there are two twins. Auto-saves on blur. */}
      {!isPortal && canEdit && (
        <label className="block" data-testid="equipment-identifier-field">
          <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Identifier
            <span className="ml-1 normal-case font-normal text-[9px] text-[#647087]/70">
              — e.g. &quot;RO 1 Bag Filter&quot; · shown next to the name everywhere
            </span>
          </span>
          <input
            type="text"
            value={identifier}
            placeholder="Add an ID label so this row is easy to spot"
            onChange={(e) => setIdentifier(e.target.value)}
            onBlur={saveIdentifier}
            disabled={identifierBusy}
            className="hv-input w-full text-sm mt-1"
            data-testid="equipment-identifier-input"
            maxLength={80}
          />
        </label>
      )}

      {!edit ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <Row label="Quantity" value={`${detail.quantity || 0} ${detail.unit || ""}`} />
          {!isPortal && (
            <Row label="Interval" value={detail.interval_days ? `${detail.interval_days} d` : "—"} />
          )}
          <Row
            label="Installed"
            value={detail.install_date ? new Date(detail.install_date).toLocaleDateString('en-GB') : "—"}
          />
          {!isPortal && (
            <Row
              label="Last serviced"
              value={detail.last_service_date ? new Date(detail.last_service_date).toLocaleDateString('en-GB') : "—"}
            />
          )}
          {!isPortal && (
            <Row
              label="Next service"
              value={detail.next_service_date ? new Date(detail.next_service_date).toLocaleDateString('en-GB') : "—"}
            />
          )}
          <Row label="Manufacturer" value={detail.manufacturer || "—"} />
          {/* Iter 400.27 — Notes visible in read-only view too. Full
              width via col-span-2 so long notes wrap comfortably. */}
          {detail.notes && (
            <div className="sm:col-span-2 mt-1 border-t border-white/5 pt-2" data-testid="equipment-notes-readonly">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-0.5">Notes</div>
              <div className="text-[12px] text-[#a0aabf] whitespace-pre-wrap">{detail.notes}</div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2.5 rounded-md border border-cyan-500/30 bg-cyan-500/[0.04] p-3" data-testid="equipment-edit-form">
          <div className="flex items-center gap-2 text-xs">
            <CalendarClock className="w-4 h-4 text-cyan-300" />
            <span className="font-bold uppercase tracking-wider text-cyan-300">Service schedule</span>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.is_service_item}
              onChange={(e) => setForm({ ...form, is_service_item: e.target.checked })}
              data-testid="equipment-edit-isservice"
              className="w-4 h-4"
            />
            <span>This item is on a recurring service schedule</span>
          </label>
          {form.is_service_item && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Interval (days)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.interval_days}
                    onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
                    placeholder="e.g. 90"
                    className="hv-input mt-1 text-sm"
                    data-testid="equipment-edit-interval"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Next service</label>
                  <DateTimePicker
                    value={form.next_service_date}
                    onChange={(v) => setForm({ ...form, next_service_date: v })}
                    showTime={false}
                    placeholder="Pick date"
                    className="mt-1"
                    testId="equipment-edit-nextdate"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">Last service</label>
                  <DateTimePicker
                    value={form.last_service_date}
                    onChange={(v) => setForm({ ...form, last_service_date: v })}
                    showTime={false}
                    placeholder="Pick date"
                    className="mt-1"
                  />
                </div>
              </div>
            </>
          )}
          {/* Iter 400.26 — Install date is editable for ANY equipment,
              even non-serviceable assets (housings, gauges, etc.). */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">Install date</label>
            <DateTimePicker
              value={form.install_date}
              onChange={(v) => setForm({ ...form, install_date: v })}
              showTime={false}
              placeholder="Pick date"
              className="mt-1"
              testId="equipment-edit-installdate"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-white/5">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Quantity</label>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="hv-input mt-1 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Unit</label>
              <input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="hv-input mt-1 text-sm"
              />
            </div>
          </div>
          {/* Iter 400.27 — Notes per equipment (free text, edit-time). */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">Notes</label>
            <DictTextarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Model, notes, quirks, install location, warranty ref, anything worth remembering…"
              className="hv-input mt-1 text-sm w-full resize-y"
              data-testid="equipment-edit-notes"
            />
          </div>
          {err && <div className="text-xs text-rose-300">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setEdit(false)} className="hv-btn hv-btn-ghost text-xs">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="equipment-edit-save"
            >
              <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {detail.description && !edit && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">
            Description
          </div>
          <div className="text-sm text-[#dde3ec] whitespace-pre-wrap leading-relaxed">
            {detail.description}
          </div>
        </div>
      )}

      {/* Service history sub-block */}
      <div className="pt-3 border-t border-white/5">
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4 text-[#1fb6c1]" />
          <h4 className="font-display text-sm font-bold">
            Service history{" "}
            <span className="text-[10px] font-mono text-[#647087]">
              ({detailLogs.length})
            </span>
          </h4>
        </div>
        {detailLogs.length === 0 ? (
          <div className="text-xs text-[#647087] italic">
            No service history for this item.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {detailLogs
              .sort((a, b) => new Date(b.service_date || 0) - new Date(a.service_date || 0))
              .map((log) => (
                <div
                  key={log.id}
                  className="px-2 py-1.5 rounded border border-white/5 bg-white/[0.02]"
                  data-testid={`service-log-${log.id}`}
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider">
                    <span className="text-[#1fb6c1]">{log.action || "serviced"}</span>
                    <span className="text-[#647087]">
                      {log.service_date ? new Date(log.service_date).toLocaleDateString('en-GB') : ""}
                    </span>
                  </div>
                  {log.notes && (
                    <div className="text-xs text-[#dde3ec] mt-0.5">{log.notes}</div>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    {log.by_user_name ? (
                      <div className="text-[10px] text-[#647087]">by {log.by_user_name}</div>
                    ) : <span />}
                    {log.jobcard_id && (
                      // Iter 427.p48 (Dirk, WS2) — clickable JC link on
                      // each service history row so ops can jump from
                      // "when was this last serviced" straight to the
                      // job card that recorded it. Portal users get the
                      // scrubbed /portal/jobcards/:id route.
                      <Link
                        to={isPortal ? `/portal/jobcards/${log.jobcard_id}` : `/jobcards/${log.jobcard_id}`}
                        className="inline-flex items-center gap-1 text-[10px] text-[#1fb6c1] hover:text-[#5fe3ec] hover:underline"
                        data-testid={`service-log-jc-link-${log.id}`}
                        title="View linked job card"
                      >
                        View JC <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2 pt-3 border-t border-white/5">
        <div className="flex gap-2">
          {canEdit && !edit && (
            <button
              type="button"
              onClick={onDelete}
              className="hv-btn hv-btn-ghost text-rose-300 text-xs"
              data-testid="equipment-detail-delete"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="equipment-detail-collapse"
          >
            <X className="w-3.5 h-3.5" /> Collapse
          </button>
        </div>
        {!isPortal && !edit && (
          <Link
            to="/scheduler"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="equipment-detail-open"
            title="Open the full Service Schedule page to see all equipment on one calendar"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in Scheduler
          </Link>
        )}
      </div>
      {/* Iter 418.u — split / merge / move-to-group modals live inside
          the detail body so they only mount when the row is expanded. */}
      {showSplit && (
        <SplitEquipmentModal
          detail={detail}
          onClose={() => setShowSplit(false)}
          onDone={() => { setShowSplit(false); onChanged?.(); }}
        />
      )}
      {showMerge && (
        <MergeEquipmentModal
          detail={detail}
          onClose={() => setShowMerge(false)}
          onDone={() => { setShowMerge(false); onClose?.(); onChanged?.(); }}
        />
      )}
      {showMoveGroup && (
        <MoveToGroupModal
          detail={detail}
          onClose={() => setShowMoveGroup(false)}
          onDone={(newParentId) => {
            setShowMoveGroup(false);
            patchEquipment({ group_of: newParentId });
          }}
        />
      )}
      {/* Iter 427.p48 (WS4) — Retire / Replace / Lineage modals. */}
      {showRetire && (
        <RetireEquipmentModal
          detail={detail}
          onClose={() => setShowRetire(false)}
          onDone={(updated) => {
            setShowRetire(false);
            setDetail((prev) => ({ ...prev, ...(updated || {}) }));
            onChanged?.();
          }}
        />
      )}
      {showReplace && (
        <ReplaceEquipmentModal
          detail={detail}
          onClose={() => setShowReplace(false)}
          onDone={() => {
            setShowReplace(false);
            onClose?.();
            onChanged?.();
          }}
        />
      )}
      {showLineage && (
        <EquipmentLineageModal
          detail={detail}
          onClose={() => setShowLineage(false)}
        />
      )}
    </div>
  );
}

/* ─────────────── Iter 418.u — Actions menu item helper ─────────────── */
function MenuItem({ onClick, icon: Icon, label, hint, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-start gap-2"
      data-testid={testId}
    >
      <Icon className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-slate-100 leading-tight">{label}</div>
        {hint && <div className="text-[10px] text-[#647087] leading-tight mt-0.5">{hint}</div>}
      </div>
    </button>
  );
}

/* ─────────────── Iter 418.u — Split modal ─────────────── */
function SplitEquipmentModal({ detail, onClose, onDone }) {
  const initialQty = Math.max(1, Math.round(Number(detail.quantity) || 1));
  const [rows, setRows] = useState(() => {
    // Default: split into `initialQty` rows of 1 each.
    const n = Math.min(initialQty, 10);
    return Array.from({ length: Math.max(2, n) }, (_, i) => ({
      quantity: n >= 2 ? 1 : Number(detail.quantity) / 2,
      identifier: "",
    }));
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const total = rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const originalQty = Number(detail.quantity) || 0;
  const balanced = Math.abs(total - originalQty) < 0.0001;

  const addRow = () => setRows((prev) => [...prev, { quantity: 1, identifier: "" }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const submit = async () => {
    if (!balanced) { setErr(`Parts must total ${originalQty}`); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/equipment/${detail.id}/split`, {
        parts: rows.map((r) => ({
          quantity: Number(r.quantity) || 0,
          identifier: r.identifier.trim() || undefined,
        })),
      });
      toast.success(`Split into ${rows.length} rows`);
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not split");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto" data-testid="equipment-split-modal">
        <div className="flex items-center gap-2 mb-3">
          <SplitSquareHorizontal className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">Split &quot;{detail.name}&quot;</h3>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          Current quantity: <b className="text-white">{originalQty}</b> {detail.unit || "pcs"}. Split
          into any combination — each new row will inherit the SKU, install
          date and interval. Give each part its own identifier now if you
          know it (e.g. &quot;RO 1&quot;, &quot;RO 2&quot;) — you can always edit later.
        </p>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`split-row-${i}`}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={r.quantity}
                onChange={(e) => updateRow(i, { quantity: e.target.value })}
                className="hv-input w-24 text-sm"
                placeholder="Qty"
                data-testid={`split-row-qty-${i}`}
              />
              <input
                type="text"
                value={r.identifier}
                onChange={(e) => updateRow(i, { identifier: e.target.value })}
                className="hv-input flex-1 text-sm"
                placeholder={`Identifier (e.g. RO ${i + 1})`}
                maxLength={80}
                data-testid={`split-row-id-${i}`}
              />
              {rows.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="hv-btn hv-btn-ghost text-rose-300 text-xs"
                  data-testid={`split-row-remove-${i}`}
                  aria-label="Remove row"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addRow}
          className="hv-btn hv-btn-ghost text-xs mt-2"
          data-testid="split-add-row"
        >
          <Plus className="w-3 h-3" /> Add row
        </button>
        <div className={`mt-3 text-xs ${balanced ? "text-emerald-300" : "text-amber-300"}`}>
          Total: {total} / {originalQty} {balanced ? "✓" : "— must match"}
        </div>
        {err && <div className="text-xs text-rose-300 mt-2" data-testid="split-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" disabled={busy}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            className="hv-btn hv-btn-primary text-xs"
            disabled={busy || !balanced || rows.length < 2}
            data-testid="split-submit"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Splitting…</> : <><SplitSquareHorizontal className="w-3 h-3" /> Split into {rows.length}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Iter 418.u — Merge modal ─────────────── */
function MergeEquipmentModal({ detail, onClose, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/equipment?customer_id=${detail.customer_id}&site_id=${detail.site_id}`);
        if (!alive) return;
        setCandidates((data || []).filter((r) => r.id !== detail.id && !r.deleted_at));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [detail.id, detail.customer_id, detail.site_id]);

  const submit = async () => {
    const sourceIds = Object.keys(selected).filter((k) => selected[k]);
    if (sourceIds.length === 0) { setErr("Pick at least one row to merge in."); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/equipment/merge`, { target_id: detail.id, source_ids: sourceIds });
      toast.success(`Merged ${sourceIds.length} row${sourceIds.length === 1 ? "" : "s"} into "${detail.name}"`);
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not merge");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto" data-testid="equipment-merge-modal">
        <div className="flex items-center gap-2 mb-3">
          <Combine className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">Merge into &quot;{detail.name}&quot;</h3>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          Pick one or more duplicate rows to fold into this one. Their
          quantities are added onto this row and their service history
          follows the survivor. The other rows are archived (recoverable
          from Trash).
        </p>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {loading && <div className="text-xs text-[#647087]"><Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Loading…</div>}
          {!loading && candidates.length === 0 && (
            <div className="text-xs text-[#647087]">No other equipment on this site to merge with.</div>
          )}
          {candidates.map((c) => (
            <label
              key={c.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
              data-testid={`merge-cand-${c.id}`}
            >
              <input
                type="checkbox"
                checked={!!selected[c.id]}
                onChange={(e) => setSelected((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                className="w-4 h-4"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">
                  {c.name}
                  {c.identifier && <span className="ml-1 text-emerald-300">· {c.identifier}</span>}
                </div>
                <div className="text-[10px] text-[#647087]">{c.quantity || 0} {c.unit || "pcs"}</div>
              </div>
            </label>
          ))}
        </div>
        {err && <div className="text-xs text-rose-300 mt-2" data-testid="merge-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" disabled={busy}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            className="hv-btn hv-btn-primary text-xs"
            disabled={busy}
            data-testid="merge-submit"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Merging…</> : <><Combine className="w-3 h-3" /> Merge</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Iter 418.u — Move-to-group modal ─────────────── */
function MoveToGroupModal({ detail, onClose, onDone }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(detail.group_of || "");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/equipment?customer_id=${detail.customer_id}&site_id=${detail.site_id}`);
        if (!alive) return;
        setCandidates((data || []).filter((r) => r.id !== detail.id && !r.deleted_at));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [detail.id, detail.customer_id, detail.site_id]);

  const groups = candidates.filter((c) => c.is_group);
  const nonGroups = candidates.filter((c) => !c.is_group);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto" data-testid="equipment-move-group-modal">
        <div className="flex items-center gap-2 mb-3">
          <FolderInput className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">
            {detail.group_of ? "Change group" : "Add to group"}
          </h3>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          Pick a parent for &quot;{detail.name}&quot;. Only equipment marked as a
          group label appears at the top — everything else on this site
          is shown below in case you want to convert one on the fly.
        </p>
        {loading && <div className="text-xs text-[#647087]"><Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Loading…</div>}
        <div className="space-y-1 max-h-72 overflow-y-auto">
          <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer">
            <input
              type="radio"
              name="parent"
              checked={selectedId === ""}
              onChange={() => setSelectedId("")}
              className="w-4 h-4"
              data-testid="move-group-detach"
            />
            <span className="text-sm">— No parent (top level)</span>
          </label>
          {groups.length > 0 && (
            <div className="text-[10px] uppercase tracking-wider text-cyan-300/70 pt-2 pb-1">Groups</div>
          )}
          {groups.map((c) => (
            <label
              key={c.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
              data-testid={`move-group-cand-${c.id}`}
            >
              <input
                type="radio"
                name="parent"
                checked={selectedId === c.id}
                onChange={() => setSelectedId(c.id)}
                className="w-4 h-4"
              />
              <span className="text-sm">{c.name}</span>
            </label>
          ))}
          {nonGroups.length > 0 && (
            <div className="text-[10px] uppercase tracking-wider text-[#647087] pt-2 pb-1">Other equipment</div>
          )}
          {nonGroups.map((c) => (
            <label
              key={c.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
              data-testid={`move-group-cand-${c.id}`}
            >
              <input
                type="radio"
                name="parent"
                checked={selectedId === c.id}
                onChange={() => setSelectedId(c.id)}
                className="w-4 h-4"
              />
              <span className="text-sm">{c.name} <span className="text-[10px] text-[#647087]">· not yet a group</span></span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
          <button
            type="button"
            onClick={async () => {
              if (selectedId && !groups.some((g) => g.id === selectedId)) {
                // Auto-flip the picked target into a group if the user
                // picked a non-group equipment.
                try {
                  await api.patch(`/equipment/${selectedId}`, { is_group: true });
                } catch { /* fall through — parent flag is best-effort */ }
              }
              onDone?.(selectedId || null);
            }}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="move-group-submit"
          >
            <FolderInput className="w-3 h-3" /> {selectedId ? "Move" : "Detach"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Iter 418.x — New Group modal ───────────────
 * Create a brand-new group container (no existing equipment
 * required). Prompts for a name (e.g. "AFM FRP"), an optional
 * identifier, a site (if the block spans multiple), and then a
 * multi-select of equipment on that site to become children.
 * On submit: POST /equipment to create the group, then PATCH each
 * selected child with group_of set.
 */
function NewGroupModal({ sites, presetSiteId, equipment, customerId, onClose, onCreated }) {
  const initialSite = presetSiteId
    || (sites?.length === 1 ? sites[0].id : null)
    || (equipment[0]?.site_id || "");
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [siteId, setSiteId] = useState(initialSite);
  const [pickedIds, setPickedIds] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const candidates = equipment.filter(
    (e) => e.site_id === siteId && !e.deleted_at && !e.is_group && !e.group_of,
  );

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setErr("Group needs a name"); return; }
    if (!siteId) { setErr("Pick a site"); return; }
    if (!customerId) { setErr("Missing customer context"); return; }
    setBusy(true);
    setErr("");
    try {
      // 1. Create the group container.
      const { data: group } = await api.post("/equipment", {
        customer_id: customerId,
        site_id: siteId,
        name: trimmed,
        identifier: identifier.trim() || undefined,
        is_group: true,
        quantity: 1,
        unit: "group",
      });
      // 2. Nest picked equipment under the new group.
      const picks = Object.keys(pickedIds).filter((k) => pickedIds[k]);
      await Promise.all(
        picks.map((cid) =>
          api.patch(`/equipment/${cid}`, { group_of: group.id }).catch(() => null),
        ),
      );
      toast.success(
        picks.length > 0
          ? `Group "${trimmed}" created with ${picks.length} child${picks.length === 1 ? "" : "ren"}`
          : `Group "${trimmed}" created — add children from any equipment's Actions menu`,
      );
      onCreated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create group");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto" data-testid="equipment-new-group-modal">
        <div className="flex items-center gap-2 mb-3">
          <FolderPlus className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">Create new group</h3>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Group name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AFM FRP"
              className="hv-input w-full text-sm mt-1"
              data-testid="new-group-name"
              maxLength={80}
              autoFocus
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Identifier (optional)</span>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="e.g. Plant 1"
              className="hv-input w-full text-sm mt-1"
              data-testid="new-group-identifier"
              maxLength={80}
            />
          </label>
          {sites && sites.length > 1 && !presetSiteId && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Site</span>
              <select
                value={siteId || ""}
                onChange={(e) => { setSiteId(e.target.value); setPickedIds({}); }}
                className="hv-input w-full text-sm mt-1"
                data-testid="new-group-site"
              >
                <option value="" disabled>—</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1">
              Add equipment to this group ({candidates.length} available)
            </div>
            {candidates.length === 0 ? (
              <div className="text-xs text-[#647087] px-2 py-3 rounded border border-white/5 bg-white/[0.02]">
                No standalone equipment at this site to nest. You can create the empty group now and add children later from each equipment&apos;s Actions menu.
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto space-y-1 rounded border border-white/5 bg-white/[0.02] p-2">
                {candidates.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                    data-testid={`new-group-pick-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!pickedIds[c.id]}
                      onChange={(e) => setPickedIds((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">
                        {c.name}
                        {c.identifier && <span className="ml-1 text-emerald-300">· {c.identifier}</span>}
                      </div>
                      <div className="text-[10px] text-[#647087]">{c.quantity || 0} {c.unit || "pcs"}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        {err && <div className="text-xs text-rose-300 mt-2" data-testid="new-group-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" disabled={busy}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            className="hv-btn hv-btn-primary text-xs"
            disabled={busy || !name.trim() || !siteId}
            data-testid="new-group-submit"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Creating…</> : <><FolderPlus className="w-3 h-3" /> Create group</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Iter 427.p48 (WS4) — Retire / Replace / Lineage ───────
 * Three lifecycle modals for the equipment detail body.
 * ---------------------------------------------------------------- */
function RetireEquipmentModal({ detail, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/equipment/${detail.id}/retire`, {
        reason: reason.trim() || null,
      });
      toast.success("Equipment retired");
      onDone?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not retire");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-md p-4" data-testid="equipment-retire-modal">
        <div className="flex items-center gap-2 mb-3">
          <Archive className="w-4 h-4 text-rose-300" />
          <h3 className="font-display text-base font-bold">Retire &quot;{detail.name}&quot;</h3>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          This equipment will be marked <b className="text-rose-300">retired</b>. Service
          history stays attached. It disappears from the default equipment list
          and from Record Service, but you can bring it back later via
          &quot;Un-retire&quot; on the same actions menu.
        </p>
        <label className="block text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1">
          Reason (optional)
        </label>
        <DictTextarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="hv-input w-full text-sm"
          placeholder="e.g. Leaking · Obsolete · Removed at customer's request"
          data-testid="equipment-retire-reason"
        />
        {err && <div className="text-xs text-rose-300 mt-2" data-testid="equipment-retire-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" disabled={busy}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="hv-btn text-xs bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/40"
            data-testid="equipment-retire-submit"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Retiring…</> : <><Archive className="w-3 h-3" /> Retire equipment</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function ReplaceEquipmentModal({ detail, onClose, onDone }) {
  const [form, setForm] = useState({
    reason: "",
    name: detail.name || "",
    model: detail.model || "",
    serial: "",
    install_date: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = {
        reason: form.reason.trim() || null,
        replacement: {
          name: form.name.trim() || null,
          model: form.model.trim() || null,
          serial: form.serial.trim() || null,
          install_date: form.install_date || null,
        },
      };
      await api.post(`/equipment/${detail.id}/replace`, payload);
      toast.success("Replaced — successor created");
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not replace");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto" data-testid="equipment-replace-modal">
        <div className="flex items-center gap-2 mb-3">
          <Recycle className="w-4 h-4 text-amber-300" />
          <h3 className="font-display text-base font-bold">Replace &quot;{detail.name}&quot;</h3>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          Retires this row and creates a fresh successor with the same site /
          customer / interval. Only fill fields that differ from the original —
          everything left blank inherits from the retired row.
        </p>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Reason</span>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="hv-input w-full text-sm mt-1"
              placeholder="e.g. End of life · Broken · Upgrade"
              data-testid="equipment-replace-reason"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">New name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="hv-input w-full text-sm mt-1"
                data-testid="equipment-replace-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Install date</span>
              <input
                type="date"
                value={form.install_date}
                onChange={(e) => setForm({ ...form, install_date: e.target.value })}
                className="hv-input w-full text-sm mt-1"
                data-testid="equipment-replace-install-date"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Model</span>
              <input
                type="text"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="hv-input w-full text-sm mt-1"
                data-testid="equipment-replace-model"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">Serial</span>
              <input
                type="text"
                value={form.serial}
                onChange={(e) => setForm({ ...form, serial: e.target.value })}
                className="hv-input w-full text-sm mt-1"
                data-testid="equipment-replace-serial"
              />
            </label>
          </div>
        </div>
        {err && <div className="text-xs text-rose-300 mt-2" data-testid="equipment-replace-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" disabled={busy}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !form.name.trim()}
            className="hv-btn text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40"
            data-testid="equipment-replace-submit"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Replacing…</> : <><Recycle className="w-3 h-3" /> Retire &amp; create successor</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function EquipmentLineageModal({ detail, onClose }) {
  const [chain, setChain] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/equipment/${detail.id}/lineage`);
        if (!cancel) setChain(data?.chain || []);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Could not load lineage");
      }
    })();
    return () => { cancel = true; };
  }, [detail.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" role="dialog">
      <div className="hv-card w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto" data-testid="equipment-lineage-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-base font-bold">Equipment lineage</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/5 rounded" data-testid="equipment-lineage-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-[#647087] mb-3">
          Every predecessor and successor of this equipment, oldest first. The
          highlighted row is the item you&apos;re viewing.
        </p>
        {err && <div className="text-xs text-rose-300" data-testid="equipment-lineage-error">{err}</div>}
        {!err && chain === null && (
          <div className="text-xs text-[#647087] italic inline-flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading lineage…
          </div>
        )}
        {chain && chain.length === 0 && (
          <div className="text-xs text-[#647087] italic">No lineage recorded.</div>
        )}
        {chain && chain.length > 0 && (
          <ol className="relative border-l border-white/10 ml-2 space-y-3" data-testid="equipment-lineage-chain">
            {chain.map((eq, idx) => {
              const isFocus = eq.id === detail.id;
              const isRetired = !!eq.retired_at;
              return (
                <li key={eq.id} className="ml-4 relative" data-testid={`equipment-lineage-node-${eq.id}`}>
                  <span
                    className={`absolute -left-[9px] top-1.5 w-3 h-3 rounded-full border-2 ${
                      isFocus
                        ? "bg-emerald-400 border-emerald-200"
                        : isRetired
                        ? "bg-rose-500/70 border-rose-300/60"
                        : "bg-cyan-400 border-cyan-200"
                    }`}
                  />
                  <div
                    className={`rounded border p-2 text-xs ${
                      isFocus
                        ? "border-emerald-500/40 bg-emerald-500/10"
                        : "border-white/10 bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-white">{eq.name}</span>
                      {eq.identifier && (
                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                          {eq.identifier}
                        </span>
                      )}
                      {isFocus && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                          You are here
                        </span>
                      )}
                      {isRetired && !isFocus && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/10 inline-flex items-center gap-1">
                          <Archive className="w-2.5 h-2.5" /> Retired
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#a0aabf] mt-1">
                      {eq.serial ? `Serial: ${eq.serial} · ` : ""}
                      {eq.install_date ? `Installed ${new Date(eq.install_date).toLocaleDateString("en-GB")}` : ""}
                      {eq.retired_at ? ` · Retired ${new Date(eq.retired_at).toLocaleDateString("en-GB")}` : ""}
                    </div>
                    {eq.retired_reason && (
                      <div className="text-[10px] text-rose-200/80 mt-0.5 italic">
                        &quot;{eq.retired_reason}&quot;
                      </div>
                    )}
                  </div>
                  {idx < chain.length - 1 && (
                    <div className="text-[10px] text-[#647087] ml-1 mt-1 inline-flex items-center gap-1">
                      <ArrowLeftRight className="w-2.5 h-2.5" /> replaced by
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}





